<!--
  HeaderFilterPills — the single header filter strip (search clients /
  portfolio / time frame). Rendered ONCE by layouts/dashboard.vue on every
  page; there is no teleport and no page-owned copy. Behaviour per page is set
  by route meta, passed down as props:

    mode          'list' → the search box filters the current page's list
                           (value lives in the shared useHeaderFilters store,
                           which the page reads).
                  'jump' → the search box navigates to a client's workspace
                           (default for every page without its own list).
    showTimeframe  whether to show the time-frame pill (list pages that are
                   time-scoped).
    clientOptions  company names for the search datalist (supplied by the
                   layout's global client list).

  The portfolio pill always shows and drives the global portfolio store; on
  change it emits `portfolio-changed` so the layout can refresh page data.
-->
<template>
  <!-- ONE composed scope bar, not three floating pills: search + portfolio +
       timeframe share a single bordered container with hairline dividers, so
       they read as one control ("what am I looking at") rather than three
       orphans. Height matches the 36px header circles. -->
  <div
    class="flex items-center h-9 min-w-0 overflow-hidden rounded-(--nviya-radius-md) border border-(--nviya-border-strong) bg-(--nviya-surface) divide-x divide-(--nviya-border) focus-within:border-(--nviya-accent) transition-colors"
  >
    <!-- Search segment -->
    <div v-if="mode !== 'none'" class="flex items-center gap-2 px-3 h-full w-64 min-w-0">
      <FontAwesome :icon="['fas', 'magnifying-glass']"
        class="w-3.5 h-3.5 shrink-0 text-(--nviya-text-tertiary)" />
      <input
        ref="searchEl"
        v-model="searchModel"
        type="text"
        :list="datalistId"
        :placeholder="mode === 'jump' ? 'Go to client…' : 'Search clients…'"
        class="flex-1 min-w-0 bg-transparent outline-none text-sm text-(--nviya-text-primary) placeholder:text-(--nviya-text-tertiary)"
        @keydown.enter="onSubmit"
        @change="onSubmit"
        @keydown.esc="searchModel = ''"
      />
      <kbd
        v-if="!searchModel"
        class="hidden lg:inline-flex items-center px-1.5 py-0.5 rounded border border-(--nviya-border) text-[10px] font-mono text-(--nviya-text-tertiary) shrink-0"
      >{{ metaKeyLabel }}K</kbd>
      <button
        v-if="mode === 'list' && searchModel"
        type="button"
        title="Clear client filter"
        class="w-4 h-4 rounded-full flex items-center justify-center shrink-0 bg-(--nviya-text-tertiary) text-white hover:bg-(--nviya-text-muted) cursor-pointer"
        @click="searchModel = ''"
      >
        <FontAwesome :icon="['fas', 'xmark']" class="w-2.5 h-2.5" />
      </button>
      <datalist :id="datalistId">
        <option v-for="c in clientOptions" :key="c" :value="c" />
      </datalist>
    </div>

    <!-- Portfolio segment (invisible select overlay) -->
    <div
      class="relative flex items-center gap-2 px-3 h-full cursor-pointer shrink-0 text-(--nviya-text-secondary) hover:bg-(--nviya-figure-bg) transition-colors"
      title="Portfolio"
    >
      <span class="font-mono text-[10px] font-bold tracking-[0.11em] text-(--nviya-text-tertiary)">PORTFOLIO</span>
      <span class="text-sm whitespace-nowrap">{{ activePortfolioLabel }}</span>
      <FontAwesome :icon="['fas', 'chevron-down']" class="w-2.5 h-2.5 shrink-0 text-(--nviya-text-tertiary)" />
      <select
        :value="portfolioStore.effectivePortfolioId"
        class="absolute inset-0 opacity-0 cursor-pointer"
        title="Switch portfolio"
        @change="changePortfolio($event.target.value)"
      >
        <option v-for="p in portfolioStore.portfolios" :key="p.id" :value="p.id">
          {{ p.name }}
        </option>
      </select>
    </div>

    <!-- Time frame segment -->
    <div
      v-if="showTimeframe"
      class="relative flex items-center gap-2 px-3 h-full cursor-pointer shrink-0 text-(--nviya-text-secondary) hover:bg-(--nviya-figure-bg) transition-colors"
      title="Time frame"
    >
      <span class="font-mono text-[10px] font-bold tracking-[0.11em] text-(--nviya-text-tertiary)">TIME</span>
      <span class="text-sm whitespace-nowrap">{{ timeframeShort }}</span>
      <FontAwesome :icon="['fas', 'chevron-down']" class="w-2.5 h-2.5 shrink-0 text-(--nviya-text-tertiary)" />
      <select
        v-model.number="timeframe"
        class="absolute inset-0 opacity-0 cursor-pointer"
        title="Time frame"
      >
        <option v-for="t in TIMEFRAMES" :key="t.days" :value="t.days">{{ t.label }}</option>
      </select>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { usePortfolioStore } from '@/store/usePortfolioStore'
import { useHeaderFilters } from '@/composables/useHeaderFilters'

const props = defineProps({
  mode: { type: String, default: 'jump' }, // 'jump' | 'list' | 'none' (page owns its client picker)
  showTimeframe: { type: Boolean, default: false },
  clientOptions: { type: Array, default: () => [] },
  datalistId: { type: String, default: 'header-client-options' },
})

const emit = defineEmits(['portfolio-changed'])

// List pages read search + timeframe from here; jump pages keep search local.
const { clientQuery, timeframe } = useHeaderFilters()
const jumpValue = ref('')

const searchModel = computed({
  get: () => (props.mode === 'list' ? clientQuery.value : jumpValue.value),
  set: (v) => {
    if (props.mode === 'list') clientQuery.value = v
    else jumpValue.value = v
  },
})

// Jump mode: resolve the typed/selected name to a real client, then navigate.
const onSubmit = () => {
  if (props.mode !== 'jump') return
  const name = (jumpValue.value || '').trim()
  if (!name) return
  const match = props.clientOptions.find((c) => c.toLowerCase() === name.toLowerCase())
  if (!match) return
  jumpValue.value = ''
  navigateTo(`/company-intelligence/${encodeURIComponent(match)}`)
}

const TIMEFRAMES = [
  { days: 1, label: 'Last 24h' },
  { days: 7, label: 'Last 7 days' },
  { days: 30, label: 'Last 30 days' },
  { days: 90, label: 'Last 90 days' },
]
const timeframeLabel = computed(() =>
  TIMEFRAMES.find((t) => t.days === timeframe.value)?.label || 'Time frame')
// In the compound bar the TIME label carries the meaning — value stays terse.
const timeframeShort = computed(() =>
  timeframe.value === 1 ? '24h' : `${timeframe.value}d`)

const portfolioStore = usePortfolioStore()
const activePortfolioLabel = computed(() =>
  portfolioStore.portfolios.find((p) => p.id === portfolioStore.effectivePortfolioId)?.name || 'Portfolio')

// ⌘K / Ctrl+K focuses the instrument from anywhere.
const searchEl = ref(null)
const metaKeyLabel = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform) ? '⌘' : 'Ctrl+'
const onGlobalKey = (e) => {
  if (props.mode === 'none') return
  if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault()
    searchEl.value?.focus()
  }
}
onMounted(() => window.addEventListener('keydown', onGlobalKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onGlobalKey))

const changePortfolio = async (id) => {
  try {
    const portfolio = portfolioStore.portfolios.find((p) => p.id === Number(id)) || null
    await portfolioStore.setActivePortfolio(Number(id))
    emit('portfolio-changed', portfolio)
  } catch (e) {
    console.error('[HeaderFilterPills] portfolio switch failed:', e)
  }
}
</script>
